import React, { useEffect, useState } from 'react'
import Layout from '@/components/layout/Layout'
import MainHeading from '@/components/common/MainHeading'
import Card from '@/components/common/Card'
import { useParams, useNavigate } from 'react-router-dom'
import { RoutesLink } from '@/utility/RouteLinks'
import EmployeeOnBoardService from '@/services/EmployeeOnBoard/EmployeeOnBoardService'
import { ThrowError } from '@/api/callApi'
import type { IEmployee } from '@/interface/employeeOnBoard'
import { Spinner } from '@/components/ui/spinner'
import { API_URL } from '@/api/constant'
import { Mail, Phone, Shield, MapPin, Briefcase, CreditCard, Heart, User, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'

const EmployeeDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [employee, setEmployee] = useState<IEmployee | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    console.log("emp", employee)

    useEffect(() => {
        console.log("EmployeeDetail rendered with employee ID URL Param:", id);
        if (!id) return;
        const fetchEmployee = async () => {
            try {
                setIsLoading(true);
                const response = await EmployeeOnBoardService.getEmployeeById(id);
                if (response?.status && response?.data) {
                    setEmployee(response.data);
                }
            } catch (error) {
                console.error("Error fetching employee details:", error);
                ThrowError(error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchEmployee();
    }, [id]);

    const formatDate = (dateStr: string | null | undefined) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
        });
    };

    if (isLoading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <Spinner className="h-8 w-8 text-primary animate-spin" />
                </div>
            </Layout>
        );
    }

    if (!employee) {
        return (
            <Layout>
                <div className="text-center py-12">
                    <h3 className="text-lg font-semibold text-gray-700">Employee not found</h3>
                    <Button onClick={() => navigate(RoutesLink.superAdminEmployees)} className="mt-4">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Employees
                    </Button>
                </div>
            </Layout>
        );
    }

    const photoUrl = employee?.profilePhoto
        ? (employee.profilePhoto.startsWith('http')
            ? employee.profilePhoto
            : `${API_URL}/${employee.profilePhoto.replace(/\\/g, '/')}`)
        : '';

    return (
        <Layout>
            <div className='space-y-6'>
                <div className="flex items-center justify-between">
                    <Button
                        variant="outline"
                        onClick={() => navigate(RoutesLink.superAdminEmployees)}
                        className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-200"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Employees
                    </Button>
                    {/* <Button
                        onClick={() => navigate(`${RoutesLink.editEmployee}/${employee.id}`)}
                        className="flex items-center gap-2"
                    >
                        <Pencil className="w-4 h-4" /> Edit Employee
                    </Button> */}
                </div>

                <MainHeading
                    title={`${employee?.name}'s Profile`}
                />

                {/* Profile Header Card */}
                <Card>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                        <div className="relative">
                            {photoUrl ? (
                                <img
                                    src={photoUrl}
                                    alt={employee?.name}
                                    className="w-24 h-24 rounded-2xl object-cover border-2 border-primary/20 shadow-lg"
                                />
                            ) : (
                                <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border-2 border-primary/20 flex items-center justify-center shadow-lg">
                                    <span className="text-3xl font-bold text-primary">
                                        {employee?.name?.charAt(0)?.toUpperCase() || 'E'}
                                    </span>
                                </div>
                            )}
                            <span className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white shadow-sm ${employee?.status === 'current' ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="text-2xl font-bold text-foreground">{employee?.name || '—'}</h2>
                            <p className="text-sm text-muted-foreground mt-0.5">{employee?.designationName || employee?.designation?.name || '—'}</p>
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3">
                                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                                    <Mail className="w-3.5 h-3.5 text-primary/70" /> {employee?.email || '—'}
                                </span>
                                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                                    <Phone className="w-3.5 h-3.5 text-primary/70" /> {employee?.phone || '—'}
                                </span>
                                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                                    <Shield className="w-3.5 h-3.5 text-primary/70" /> {employee?.designation?.name || '—'}
                                </span>
                            </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase
                                ${employee?.status === 'current'
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                    : 'bg-slate-50 text-slate-600 border border-slate-200'
                                }`}>
                                {employee?.status || '—'}
                            </span>
                            <span className="text-xs text-muted-foreground">{employee?.employeeCode || ''}</span>
                        </div>
                    </div>
                </Card>

                {/* Personal Information */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <User className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">Personal Information</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                        <InfoItem label="Full Name" value={employee?.name} />
                        <InfoItem label="Email" value={employee?.email} />
                        <InfoItem label="Phone" value={employee?.phone} />
                        <InfoItem label="Gender" value={employee?.gender === 0 ? 'Male' : 'Female'} />
                        <InfoItem label="Date of Birth" value={formatDate(employee?.dob)} />
                        <InfoItem label="Marital Status" value={employee?.isMarried ? 'Married' : 'Unmarried'} />
                        <InfoItem label="Father's Name" value={employee?.userDetail?.fatherName} />
                        <InfoItem label="Mother's Name" value={employee?.userDetail?.motherName} />
                        <InfoItem label="Blood Group" value={employee?.bloodGroup} />
                    </div>
                </Card>

                {/* Employment Details */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <Briefcase className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">Employment Details</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                        <InfoItem label="Employee Code" value={employee?.employeeCode} />
                        <InfoItem label="Department" value={employee?.department?.name} />
                        <InfoItem label="Designation" value={employee?.designation?.name} />
                        <InfoItem label="Branch" value={employee?.site?.name} />
                        <InfoItem label="Campus" value={employee?.campus?.name} />
                        <InfoItem label="Joining Date" value={formatDate(employee?.joiningDate)} />
                        <InfoItem label="Status" value={employee?.status} />
                    </div>
                </Card>

                {/* Emergency Contact */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <Phone className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">Emergency Contact</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                        <InfoItem label="Contact Person" value={employee?.userDetail?.ecpName} />
                        <InfoItem label="Contact Number" value={employee?.userDetail?.ecpPhone} />
                    </div>
                </Card>

                {/* Address Details */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <MapPin className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">Address Details</h3>
                    </div>

                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Present Address</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5 mb-6">
                        <InfoItem label="Address" value={employee?.userDetail?.address} />
                        <InfoItem label="State" value={employee?.userDetail?.state} />
                        <InfoItem label="City" value={employee?.userDetail?.city} />
                        <InfoItem label="Pin Code" value={employee?.userDetail?.pinCode} />
                    </div>

                    <div className="border-t border-border/50 pt-5">
                        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                            Permanent Address
                            {employee?.userDetail?.isSameAsPermanentAddress && (
                                <span className="ml-2 text-xs font-normal text-primary">(Same as present)</span>
                            )}
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                            <InfoItem label="Address" value={employee?.userDetail?.permanentAddress} />
                            <InfoItem label="State" value={employee?.userDetail?.permanentState} />
                            <InfoItem label="City" value={employee?.userDetail?.permanentCity} />
                            <InfoItem label="Pin Code" value={employee?.userDetail?.permanentPinCode} />
                        </div>
                    </div>
                </Card>

                {/* ID & Documents */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <Shield className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">ID & Documents</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                        <InfoItem label="Aadhar Number" value={employee?.userDetail?.aadharNumber} />
                        <InfoItem label="PAN Number" value={employee?.userDetail?.panNumber} />
                        <InfoItem label="UAN Number" value={employee?.userDetail?.uanNumber} />
                        <InfoItem label="ESIC Number" value={employee?.userDetail?.esicNumber} />
                        <InfoItem label="PF Number" value={employee?.userDetail?.pfNumber} />
                        <InfoItem label="Insurance Number" value={employee?.userDetail?.insuranceNumber} />
                        <InfoItem label="Driving License" value={employee?.userDetail?.drivingLicenseNumber} />
                    </div>
                </Card>

                {/* Bank Details */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <CreditCard className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">Bank Details</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                        <InfoItem label="Bank Name" value={employee?.userDetail?.bankName} />
                        <InfoItem label="Account Number" value={employee?.userDetail?.accountNumber} />
                        <InfoItem label="Account Holder" value={employee?.userDetail?.accountHolderName} />
                        <InfoItem label="IFSC/RTGS Code" value={employee?.userDetail?.ifscCode} />
                        <InfoItem label="Branch Address" value={employee?.userDetail?.branchAddress} />
                    </div>
                </Card>

                {/* Nominee Details */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <Heart className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">Nominee Details</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                        <InfoItem label="Nominee Name" value={employee?.userDetail?.nomineeName} />
                        <InfoItem label="Relation" value={employee?.userDetail?.nomineeRelation} />
                        <InfoItem label="Phone" value={employee?.userDetail?.nomineePhone} />
                        <InfoItem label="Date of Birth" value={formatDate(employee?.userDetail?.nomineeDob)} />
                    </div>
                </Card>
            </div>
        </Layout>
    )
}

/** Reusable info display item */
const InfoItem: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
    <div>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
        <p className="text-sm font-medium text-foreground">{value || '—'}</p>
    </div>
);

export default EmployeeDetail
